import { definitions } from '../definitions/definitions';
import {
  AnimatableObjects,
  AnimationAPI,
  AnimationDefinition,
  AnimationProperties,
  Frame,
  SequenceInstance,
  TransitionDefinition,
  TweenEngineGreenSockInstance,
  ViewMode,
} from '../types';
import { toArray } from '@wix/wow-utils';
import { getAnimationMode } from '../utils/definitionsUtils';
import { validateSchema } from '../utils/validationUtils';
import { viewerDefaults } from '../utils/viewerDefaults';

/**
 * Animations constructor
 */
export class AnimationsKit {
  constructor(
    engine: TweenEngineGreenSockInstance,
    frame: Frame = window,
    viewMode: ViewMode = 'desktop',
    isMotion: boolean = false,
  ) {
    this.engine = engine;
    this.frame = frame;

    this.engine.adjustLagSmoothing(500, 33);

    const animationMode = getAnimationMode(isMotion ? 'motion' : viewMode);
    this.defs = definitions[animationMode];

    this.validateAnimation = this.validateAnimation.bind(this);
    this.animate = this.animate.bind(this);
    this.transition = this.transition.bind(this);
    this.updateViewMode = this.updateViewMode.bind(this);
    this.sequence = this.sequence.bind(this);
    this.getProperties = this.getProperties.bind(this);
    this.getApiForAnimation = this.getApiForAnimation.bind(this);

    this.addTickerEvent = engine.addTickerEvent;
    this.removeTickerEvent = engine.removeTickerEvent;
    this.kill = engine.kill;
    this.delayedCall = engine.delayedCall;
    this.animateTimeScale = engine.animateTimeScale;
    this.viewerDefaults = viewerDefaults;
  }
  public engine: TweenEngineGreenSockInstance;
  public frame: Frame;
  public defs: Record<string, TransitionDefinition | AnimationDefinition>;
  // | AnimationsDefinitions[keyof AnimationsDefinitions]
  // | Record<TransitionDefinition['name'], TransitionDefinition>;

  removeTickerEvent: (callback: GSAPTickerCallback) => void;
  kill: (src: GSAPTween | GSAPTimeline, seekTo?: number) => void;
  delayedCall: (
    delay: number,
    callback: GSAPCallback,
    params: any,
    scope: any,
  ) => GSAPTween;
  animateTimeScale: (
    src: GSAPTween | GSAPTimeline,
    duration: number,
    from: number,
    to: number,
    easing?: string,
    callbacks?: {
      onStart?: GSAPCallback;
      onUpdate?: GSAPCallback;
      onComplete?: GSAPCallback;
    },
  ) => GSAPTween;

  addTickerEvent: (callback: GSAPTickerCallback) => void;
  viewerDefaults;

  validateAnimation(name: string, params: any) {
    const properties = this.getProperties(name);
    if (!properties) {
      console.log(`No such animation "${name}"`);
      return false;
    }

    return validateSchema(properties.schema || {}, params, (errors: any[]) =>
      errors.forEach((error) => console.error(error)),
    );
  }

  animate<Definition extends AnimationDefinition>(
    name: Definition['name'],
    elements: HTMLElement[] | HTMLElement | HTMLCollection | AnimatableObjects,
    duration?: number,
    delay: number = 0,
    params = {},
  ): ReturnType<Definition['animate']> {
    if (this.validateAnimation(name, { duration, delay, ...params })) {
      return (this.defs[name] as Definition).animate(
        this,
        toArray<typeof elements>(elements),
        duration,
        delay,
        params,
      ) as ReturnType<Definition['animate']>;
    }

    return (this.defs.BaseNone as AnimationDefinition).animate(
      this,
      toArray<typeof elements>(elements),
      0,
      0,
      {},
    ) as ReturnType<Definition['animate']>;
  }

  transition(
    name: string,
    sourceElements: HTMLElement[] | HTMLElement | HTMLCollection,
    destinationElements: HTMLElement[] | HTMLElement | HTMLCollection,
    duration: number,
    delay: number = 0,
    params: any = {},
  ): GSAPTimeline {
    if (this.validateAnimation(name, { duration, delay, ...params })) {
      return (this.defs[name] as TransitionDefinition).transition(
        this,
        toArray(sourceElements) as HTMLElement[],
        toArray(destinationElements) as HTMLElement[],
        duration,
        delay,
        params,
      );
    }
    return (this.defs.noTransition as TransitionDefinition).transition(
      this,
      sourceElements,
      destinationElements,
      0,
      0,
      {},
    );
  }

  updateViewMode(mode: ViewMode, frame: Frame = window) {
    const animationMode = getAnimationMode(mode);
    this.defs = definitions[animationMode];

    if (frame) {
      this.frame = frame;
    }
  }

  sequence(params?: { [key: string]: any }): SequenceInstance {
    return this.engine.sequence(params);
  }

  getProperties(name: string): Partial<AnimationProperties> {
    return this.defs[name].properties || {};
  }

  getApiForAnimation(name: string): Partial<AnimationAPI> {
    return this.defs[name].api || {};
  }

  _resetRegistrations() {
    this.defs = {};
  }
}
